import type { Lang } from "./i18n";

type HeraldHandle = {
  destroy: () => void;
  setLang?: (lang: string) => void;
};

declare global {
  interface Window {
    __HH_C?: {
      mount: (opts: {
        apiBase: string;
        lang: string;
        getAccessToken: () => Promise<string | null>;
      }) => HeraldHandle;
    };
  }
}

let handle: HeraldHandle | null = null;
let lastToken: string | null = null;

export function destroyHerald(): void {
  try {
    handle?.destroy();
  } catch {
    /* strip is optional */
  }
  handle = null;
  lastToken = null;
}

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.__HH_C) {
      resolve();
      return;
    }
    const s = document.createElement("script");
    s.src = src;
    s.async = true;
    s.dataset.hhChrome = "1";
    const timer = window.setTimeout(() => {
      s.onload = null;
      s.onerror = null;
      reject(new Error("timeout"));
    }, 8000);
    s.onload = () => {
      window.clearTimeout(timer);
      resolve();
    };
    s.onerror = () => {
      window.clearTimeout(timer);
      reject(new Error("load"));
    };
    document.body.appendChild(s);
  });
}

/** Bar tears itself down on null — only a real 401/403 may return null; blips keep the last token. */
async function readToken(): Promise<string | null> {
  try {
    const res = await fetch("/api/auth/chrome", {
      credentials: "include",
      headers: { Accept: "application/json" },
      cache: "no-store",
    });
    if (res.status === 401 || res.status === 403) {
      lastToken = null;
      return null;
    }
    if (!res.ok) return lastToken;
    const json = (await res.json()) as { t?: string };
    lastToken = json.t ? String(json.t) : null;
    return lastToken;
  } catch {
    return lastToken;
  }
}

/** Authed strip via Hiera session BFF. Guests leave the page unchanged. */
export async function syncHerald(lang: Lang): Promise<void> {
  const ui = lang === "en" ? "en" : "ru";
  if (handle?.setLang) {
    handle.setLang(ui);
    return;
  }
  try {
    const res = await fetch("/api/auth/chrome", {
      credentials: "include",
      headers: { Accept: "application/json" },
      cache: "no-store",
    });
    if (!res.ok) return;
    const cfg = (await res.json()) as { s?: string; c?: string; a?: string; t?: string };
    if (!cfg.s || !cfg.c || !cfg.a || !cfg.t) return;
    lastToken = cfg.t;
    if (!document.querySelector("link[data-hh-chrome]")) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = cfg.c;
      link.dataset.hhChrome = "1";
      document.head.appendChild(link);
    }
    await loadScript(cfg.s);
    const api = window.__HH_C;
    if (!api) return;
    destroyHerald();
    handle = api.mount({
      apiBase: cfg.a,
      lang: ui,
      getAccessToken: readToken,
    });
  } catch {
    /* strip is optional */
  }
}
